import type { TFunction } from 'i18next';
import { ChevronDown, ChevronRight, Focus, RotateCcw, Search, X, ZoomIn } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { useIfcFeatures } from '@/context/IfcFeatureContext';
import type { IfcElement, IfcIndex } from '@/helper/ifc/index';
import { searchElements } from '@/helper/ifc/search';
import {
	NO_VALUE,
	resetVisibility,
	resolveHidden,
	setGroupKey,
	storeyCheckState,
	storeyLabel,
	toggleGroup,
	toggleIsolate,
	toggleStorey,
	type IfcTreeGroup,
	type IfcTreeStorey,
	type IfcVisibilityState,
	UNPLACED_LABEL,
} from '@/helper/ifc/tree';
import { unionBbox } from '@/helper/ifc/zoom';
import { cn } from '@/lib/utils';

const SEARCH_DEBOUNCE_MS = 300;
const GROUP_PAGE_SIZE = 200;

const bboxOf = (index: IfcIndex, featureIds: number[]) =>
	unionBbox(featureIds.map((id) => index.elements[id]?.bbox));

/** The tree's own labels are also keys; only what is shown is translated. */
const displayLabel = (t: TFunction, label: string) => {
	if (label === NO_VALUE) return t('viewer.ifc.structure.noValue');
	if (label === UNPLACED_LABEL) return t('viewer.ifc.structure.unplaced');
	return label;
};

export default function IfcStructureTab({ tilesetKey }: { tilesetKey: string }) {
	const { t } = useTranslation();
	const { entries, trees, visibility, updateVisibility, zoomTo, select, openPanel } =
		useIfcFeatures();
	const entry = entries[tilesetKey];
	const tree = trees[tilesetKey];
	const state = visibility[tilesetKey];
	const index = entry?.index ?? null;

	const [query, setQuery] = useState('');
	const [debouncedQuery, setDebouncedQuery] = useState('');
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	useEffect(() => {
		const timer = setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timer);
	}, [query]);

	const results = useMemo(
		() => (index && debouncedQuery.trim() ? searchElements(index, debouncedQuery) : null),
		[index, debouncedQuery]
	);
	const hiddenTotal = useMemo(
		() => (tree && state ? resolveHidden(tree, state).size : 0),
		[tree, state]
	);

	if (!entry) return null;
	if (entry.status === 'loading') {
		return <p className="p-4 text-xs text-muted-foreground animate-pulse">{t('viewer.ifc.structure.loading')}</p>;
	}
	if (!index || !tree || !state) {
		return (
			<p className="p-4 text-xs text-muted-foreground">{t('viewer.ifc.structure.unavailable')}</p>
		);
	}

	const update = (fn: (s: IfcVisibilityState) => IfcVisibilityState) =>
		updateVisibility(tilesetKey, fn);

	const pickResult = (element: IfcElement) => {
		select({
			key: tilesetKey,
			featureId: element.featureId,
			summary: { IfcClass: element.ifcClass, Name: element.name, GlobalId: element.globalId },
		});
		zoomTo(tilesetKey, element.bbox);
		openPanel(tilesetKey, 'properties');
	};

	return (
		<div className="flex flex-col gap-2 p-3">
			<div className="relative">
				<Search size={14} className="absolute left-2 top-2 text-muted-foreground" />
				<Input
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder={t('viewer.ifc.structure.searchPlaceholder')}
					className="h-8 pl-7 pr-7 text-xs"
				/>
				{query && (
					<button
						type="button"
						className="absolute right-2 top-2 text-muted-foreground hover:text-foreground"
						onClick={() => setQuery('')}
						title={t('common.actions.clear')}
					>
						<X size={14} />
					</button>
				)}
			</div>

			{results ? (
				<div className="space-y-1">
					<div className="text-[11px] text-muted-foreground">
						{t('viewer.ifc.structure.matches', { shown: results.elements.length, total: results.total })}
					</div>
					{results.elements.map((element) => (
						<button
							key={element.featureId}
							type="button"
							onClick={() => pickResult(element)}
							className="flex w-full flex-col rounded px-2 py-1 text-left hover:bg-muted/50"
						>
							<span className="truncate text-xs font-medium" title={element.name}>
								{element.name || element.globalId || `#${element.featureId}`}
							</span>
							<span className="truncate text-[11px] text-muted-foreground">
								{element.ifcClass}
								{index.storeyOfElement[element.featureId] >= 0
									? ` · ${displayLabel(t, storeyLabel(index, index.storeys[index.storeyOfElement[element.featureId]]))}`
									: ''}
							</span>
						</button>
					))}
					{results.total === 0 && <p className="text-xs text-muted-foreground">{t('viewer.ifc.structure.noMatches')}</p>}
				</div>
			) : (
				<>
					<div className="flex items-center gap-2">
						<span className="text-[11px] text-muted-foreground whitespace-nowrap">{t('viewer.ifc.structure.groupBy')}</span>
						<Select value={state.groupKey} onValueChange={(key) => update((s) => setGroupKey(s, key))}>
							<SelectTrigger className="h-7 text-xs">
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{index.propertyKeys.map((key) => (
									<SelectItem key={key} value={key} className="text-xs">
										{key}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>

					<div className="space-y-0.5">
						{tree.storeys.map((storey) => (
							<StoreyRow
								key={`${tree.groupKey}:${storey.key}`}
								storey={storey}
								state={state}
								expanded={expanded[storey.key] ?? (tree.storeys.length === 1)}
								onToggleExpanded={() =>
									setExpanded((prev) => ({
										...prev,
										[storey.key]: !(prev[storey.key] ?? (tree.storeys.length === 1)),
									}))
								}
								onToggle={() => update((s) => toggleStorey(s, storey))}
								onToggleGroup={(group) => update((s) => toggleGroup(s, storey, group))}
								onIsolate={(key) => update((s) => toggleIsolate(s, key))}
								onZoom={(featureIds) => zoomTo(tilesetKey, bboxOf(index, featureIds))}
							/>
						))}
					</div>

					<div className="flex items-center justify-between border-t border-border pt-2">
						<span className="text-[11px] text-muted-foreground">
							{t('viewer.ifc.structure.hidden', { count: hiddenTotal })}
						</span>
						<Button
							size="sm"
							variant="ghost"
							className="h-7 text-xs"
							disabled={state.hidden.size === 0 && !state.isolate}
							onClick={() => update(resetVisibility)}
						>
							<RotateCcw className="h-3.5 w-3.5 mr-1" /> {t('common.actions.reset')}
						</Button>
					</div>
				</>
			)}
		</div>
	);
}

type StoreyRowProps = {
	storey: IfcTreeStorey;
	state: IfcVisibilityState;
	expanded: boolean;
	onToggleExpanded: () => void;
	onToggle: () => void;
	onToggleGroup: (group: IfcTreeGroup) => void;
	onIsolate: (key: string) => void;
	onZoom: (featureIds: number[]) => void;
};

function StoreyRow({
	storey,
	state,
	expanded,
	onToggleExpanded,
	onToggle,
	onToggleGroup,
	onIsolate,
	onZoom,
}: StoreyRowProps) {
	const { t } = useTranslation();
	// Grouping by a per-element key (GlobalId, Name) yields one group per element; render
	// them in pages so a large model does not put thousands of rows in the DOM at once.
	const [shown, setShown] = useState(GROUP_PAGE_SIZE);
	const check = storeyCheckState(storey, state);
	return (
		<div>
			<Row
				level={0}
				checked={check === 'checked' ? true : check === 'indeterminate' ? 'indeterminate' : false}
				label={displayLabel(t, storey.label)}
				count={storey.count}
				isolated={state.isolate === storey.key}
				onCheck={onToggle}
				onIsolate={() => onIsolate(storey.key)}
				onZoom={() => onZoom(storey.groups.flatMap((g) => g.featureIds))}
				chevron={
					<button
						type="button"
						onClick={onToggleExpanded}
						className="p-0.5 text-muted-foreground hover:text-foreground"
						title={expanded ? t('viewer.ifc.structure.collapse') : t('viewer.ifc.structure.expand')}
					>
						{expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
					</button>
				}
			/>
			{expanded &&
				storey.groups.slice(0, shown).map((group) => (
					<Row
						key={group.key}
						level={1}
						checked={!state.hidden.has(storey.key) && !state.hidden.has(group.key)}
						label={displayLabel(t, group.value)}
						count={group.count}
						isolated={state.isolate === group.key}
						onCheck={() => onToggleGroup(group)}
						onIsolate={() => onIsolate(group.key)}
						onZoom={() => onZoom(group.featureIds)}
					/>
				))}
			{expanded && storey.groups.length > shown && (
				<button
					type="button"
					onClick={() => setShown((n) => n + GROUP_PAGE_SIZE)}
					className="ml-5 px-1 py-0.5 text-[11px] text-primary hover:underline"
				>
					{t('viewer.ifc.structure.loadMore', { shown, total: storey.groups.length })}
				</button>
			)}
		</div>
	);
}

type RowProps = {
	level: 0 | 1;
	checked: boolean | 'indeterminate';
	label: string;
	count: number;
	isolated: boolean;
	onCheck: () => void;
	onIsolate: () => void;
	onZoom: () => void;
	chevron?: ReactNode;
};

function Row({
	level,
	checked,
	label,
	count,
	isolated,
	onCheck,
	onIsolate,
	onZoom,
	chevron,
}: RowProps) {
	const { t } = useTranslation();
	return (
		<div
			className={cn(
				'group flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-muted/40',
				level === 1 && 'ml-5'
			)}
		>
			{chevron ?? <span className="w-[18px]" />}
			<Checkbox checked={checked} onCheckedChange={onCheck} className="size-3.5" />
			<span
				className={cn('min-w-0 flex-1 truncate text-xs', level === 0 && 'font-medium')}
				title={label}
			>
				{label}
			</span>
			<span className="text-[11px] text-muted-foreground">{count}</span>
			<button
				type="button"
				onClick={onZoom}
				className={cn(
					'rounded p-0.5 text-muted-foreground opacity-0',
					'hover:text-primary group-hover:opacity-100'
				)}
				title={t('viewer.ifc.zoomTo')}
			>
				<ZoomIn size={13} />
			</button>
			<button
				type="button"
				onClick={onIsolate}
				className={cn(
					'rounded p-0.5 hover:text-primary',
					isolated
						? 'text-primary opacity-100'
						: 'text-muted-foreground opacity-0 group-hover:opacity-100'
				)}
				title={isolated ? t('viewer.ifc.structure.showAll') : t('viewer.ifc.structure.isolate')}
			>
				<Focus size={13} />
			</button>
		</div>
	);
}
